/**
 * Supabase in the browser — for signing in, and nothing else.
 *
 * The client below is built with the publishable key, which is public by
 * design. It cannot read `app`: the `authenticated` role holds no grant on that
 * schema (revoked in 20260923140000_api_only.sql, D-026), so a query from here
 * would be refused at the schema level. Every read and write of data goes
 * through our API with the session's access token attached — see api.ts.
 *
 * What this file owns: creating the client, the current session, and the
 * code sign-in / sign-out calls. Nothing here decides what a user may do; the API
 * decides that after verifying the token.
 */

import { createClient, type AuthChangeEvent, type Session } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { supabasePublishableKey, supabaseUrl } from './config.js';

type AuthClient = ReturnType<typeof createClient>;

let client: AuthClient | undefined;

/** The one client, created on first use so a missing setting fails at the call, not at import. */
export function supabase(): AuthClient {
  client ??= createClient(supabaseUrl(), supabasePublishableKey(), {
    auth: {
      // PKCE is the right flow for a browser SPA: the authorization code
      // travels in the URL, the token never does.
      flowType: 'pkce',
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });
  return client;
}

/** The current session's access token, or null when nobody is signed in. */
export async function currentAccessToken(): Promise<string | null> {
  const { data } = await supabase().auth.getSession();
  return data.session?.access_token ?? null;
}

/**
 * Sign-in, step two (D-066): the six-digit code from the email becomes a
 * session. The code was asked for through our API (`requestSignInCode`); there
 * is no other way to sign in — no password, no Google.
 */
export async function verifyEmailCode(email: string, code: string): Promise<void> {
  const { error } = await supabase().auth.verifyOtp({ email, token: code, type: 'email' });
  if (error) throw error;
}

export async function signOut(): Promise<void> {
  // This browser only: supabase-js defaults to 'global', which revokes every device's session, and
  // the 401 sign-out in session.tsx would let one expired tab sign the person out everywhere.
  const { error } = await supabase().auth.signOut({ scope: 'local' });
  if (error) throw error;
}

export interface SessionState {
  /** `undefined` while the stored session is still being read. */
  readonly session: Session | null | undefined;
  readonly lastEvent: AuthChangeEvent | undefined;
}

/**
 * The session as React state. Resolves once from storage, then follows every
 * auth event (sign-in, token refresh, sign-out, the OAuth redirect landing).
 */
export function useSession(): SessionState {
  const [state, setState] = useState<SessionState>({ session: undefined, lastEvent: undefined });

  useEffect(() => {
    let cancelled = false;
    void supabase()
      .auth.getSession()
      .then(({ data }) => {
        if (!cancelled) setState((s) => (s.session === undefined ? { ...s, session: data.session } : s));
      });

    const { data: subscription } = supabase().auth.onAuthStateChange((event, session) => {
      if (!cancelled) setState({ session, lastEvent: event });
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  return state;
}
